import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to initialize TomSelect on an element
 * @param {Object} options - TomSelect options
 * @returns {Object} - ref to attach to the select element
 */
const useTomSelect = (options = {}) => {
  const selectRef = useRef(null);
  const tomSelectInstanceRef = useRef(null);
  const initializedRef = useRef(false);
  const hasBeenDestroyedRef = useRef(false);
  const onChangeCallbackRef = useRef(options.onChange);
  
  // Update the ref if onChange changes
  useEffect(() => {
    onChangeCallbackRef.current = options.onChange;
  }, [options.onChange]);

  useEffect(() => {
    // Only initialize TomSelect when the component mounts and the element is ready
    const initializeTomSelect = () => {
      // Don't attempt to initialize if there's no element or TomSelect isn't available
      if (!window.TomSelect || !selectRef.current) {
        return;
      }

      // If already initialized, avoid duplicate initialization
      if (initializedRef.current && tomSelectInstanceRef.current) {
        return;
      }

      // Safety check - clean up any existing instance on this element first
      if (selectRef.current.tomselect) {
        // If there's already a TomSelect instance, destroy it first
        try {
          selectRef.current.tomselect.destroy();
        } catch (error) {
          console.warn('Error destroying existing TomSelect instance:', error);
        }
      }

      // Default options
      const defaultOptions = {
        copyClassesToDropdown: false,
        dropdownParent: 'body',
        controlInput: '<input>',
        allowEmptyOption: true,
        hideSelected: false,
        items: [],
        plugins: ['clear_button'],
        render: {
          item: function (data, escape) {
            if (data.customProperties) {
              return '<div><span class="dropdown-item-indicator">' + data.customProperties + '</span>' + escape(data.text) + '</div>';
            }
            return '<div>' + escape(data.text) + '</div>';
          },
          option: function (data, escape) {
            if (data.customProperties) {
              return '<div><span class="dropdown-item-indicator">' + data.customProperties + '</span>' + escape(data.text) + '</div>';
            }
            return '<div>' + escape(data.text) + '</div>';
          },
          no_results: function() {
            return '<div class="no-results">No results found</div>';
          }
        },
      };

      try {
        // Get placeholder from disabled+selected option if not provided
        if (!options.placeholder && selectRef.current) {
          const placeholderOption = selectRef.current.querySelector('option[value=""][disabled][selected]');
          if (placeholderOption) {
            options.placeholder = placeholderOption.textContent;
          }
        }

        // Completely hide the original select element to prevent duplicate displays
        selectRef.current.style.display = 'none';

        // Initialize new TomSelect instance with merged options
        const mergedOptions = { ...defaultOptions, ...options };
        
        // Handle onChange separately to prevent issues with form state
        if (onChangeCallbackRef.current) {
          const originalOnChange = mergedOptions.onChange;
          mergedOptions.onChange = function(value) {
            if (originalOnChange) originalOnChange(value);
            // Use the current ref value to ensure we have the latest callback
            if (onChangeCallbackRef.current) {
              onChangeCallbackRef.current(value);
            }
          };
        }
        
        tomSelectInstanceRef.current = new window.TomSelect(
          selectRef.current,
          mergedOptions
        );
        
        // Clear selection to show placeholder
        tomSelectInstanceRef.current.clear(true);
        initializedRef.current = true;
        hasBeenDestroyedRef.current = false;
      } catch (error) {
        console.error('Error initializing TomSelect:', error);
        // If initialization fails, show the original select
        if (selectRef.current) {
          selectRef.current.style.display = '';
        }
      }
    };

    // Initialize TomSelect with a small delay to ensure the DOM is ready
    const timeoutId = setTimeout(() => {
      initializeTomSelect();
    }, 100);

    // Listen for modal events if we're in a modal
    const handleModalShown = () => {
      // Re-initialize TomSelect when modal is shown if it was destroyed
      if ((!initializedRef.current || hasBeenDestroyedRef.current) && selectRef.current) {
        // Use a slightly longer delay for modal shown events
        setTimeout(() => {
          initializeTomSelect();
        }, 150);
      }
    };

    // Try to find a parent modal if it exists
    let parentModal = null;
    if (selectRef.current) {
      parentModal = selectRef.current.closest('.modal');
      if (parentModal) {
        parentModal.addEventListener('shown.bs.modal', handleModalShown);
      }
    }

    // Clean up function to prevent memory leaks
    return () => {
      clearTimeout(timeoutId);
      
      // Remove modal event listener if it was added
      if (parentModal) {
        parentModal.removeEventListener('shown.bs.modal', handleModalShown);
      }
      
      // Destroy TomSelect instance
      if (tomSelectInstanceRef.current) {
        try {
          tomSelectInstanceRef.current.destroy();
          tomSelectInstanceRef.current = null;
          initializedRef.current = false;
          hasBeenDestroyedRef.current = true;
          
          // Restore the original select visibility
          if (selectRef.current) {
            selectRef.current.style.display = '';
          }
        } catch (error) {
          console.warn('Error destroying TomSelect instance:', error);
        }
      }
    };
  }, []); // Only run on mount and unmount

  // Handle the case when the component is mounted with initial values
  useEffect(() => {
    if (tomSelectInstanceRef.current && initializedRef.current && selectRef.current) {
      // Check if the select element has a selected value
      const selectedValue = selectRef.current.value;
      if (selectedValue && selectedValue !== '') {
        // Set the value in TomSelect
        try {
          tomSelectInstanceRef.current.setValue(selectedValue, true);
        } catch (error) {
          console.warn('Error setting initial value in TomSelect:', error);
        }
      }
    }
  }, []);

  // Method to update the TomSelect value programmatically
  const setValue = useCallback((value) => {
    if (tomSelectInstanceRef.current) {
      try {
        tomSelectInstanceRef.current.setValue(value, true);
      } catch (error) {
        console.warn('Error setting value in TomSelect:', error);
      }
    }
  }, []);

  // Return both the ref and setValue method
  return selectRef;
};

export default useTomSelect; 